import Tabs from "@/components/Tabs";
import { Path, useNavigate } from "@/router";
import { useLocation, useParams } from "react-router-dom";
import { useQuery } from "@apollo/client";
import { ELIGIBLE_FOR_ENROLLMENT_ROSTER_STATUS, UNIVERSITY_ROSTER_ROWS_EXISTS_QUERY } from "./queries";

interface StudentsTabLayoutProps {
  children: React.ReactNode;
  universityId?: string;
}

export default function StudentsTabLayout({ children, universityId: actualUniversityId }: StudentsTabLayoutProps) {
  const { universityId } = useParams();
  const location = useLocation();
  const { pathname } = location;
  const currentUrl = pathname.replace(/\/$/, "");
  const navigate = useNavigate();

  // Check if there are any eligible students for enrollment using lightweight query
  const { data: rosterData } = useQuery(UNIVERSITY_ROSTER_ROWS_EXISTS_QUERY, {
    variables: {
      universityId: actualUniversityId || "",
      rosterStatus: ELIGIBLE_FOR_ENROLLMENT_ROSTER_STATUS
    },
    skip: !actualUniversityId
  });

  const hasEligibleStudents = (rosterData?.university?.universityRosterRows?.totalCount ?? 0) > 0;

  if (!universityId) return null;

  const tabItems = [
    {
      label: "Current Students",
      to: "/education/universities/:universityId/students/current" as Path,
      active: currentUrl === `/education/universities/${universityId}/students/current`
    },
    // Only show "Eligible for Enrollment" tab if there are eligible students
    ...(hasEligibleStudents
      ? [
          {
            label: "Eligible for Enrollment",
            to: "/education/universities/:universityId/students/eligible-for-enrollment" as Path,
            active: currentUrl === `/education/universities/${universityId}/students/eligible-for-enrollment`
          }
        ]
      : []),
    {
      label: "Probations",
      to: "/education/universities/:universityId/students/probations" as Path,
      active: currentUrl === `/education/universities/${universityId}/students/probations`
    },
    {
      label: "Eligible for Graduation",
      to: "/education/universities/:universityId/students/graduation-eligible" as Path,
      active: currentUrl === `/education/universities/${universityId}/students/graduation-eligible`
    },
    {
      label: "Alumni",
      to: "/education/universities/:universityId/students/alumni" as Path,
      active: currentUrl === `/education/universities/${universityId}/students/alumni`
    }
  ];

  return (
    <>
      <Tabs
        tabItems={tabItems.map(({ label, to, active }) => ({
          label,
          onClick: () => navigate(to, { params: { universityId } }),
          active
        }))}
      />
      <div className="flex flex-col" data-testid="tab-content">
        {children}
      </div>
    </>
  );
}
